// Version lifecycle actions (Admin) for one document version: a menu with Promote to in force, Set status and Delete version, each opening its dialog; lifecycle changes need a writable server, deleting also needs a job launcher, and unavailable items say why.
import { ArrowUpCircleIcon, EllipsisIcon, ListChecksIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";

import type { DocumentStatus } from "@/api/__generated__";
import { useActionAvailability } from "@/app/session";
import { RequireRole } from "@/components/common/RequireRole";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DeleteVersionDialog } from "@/features/jobs/dialogs/DeleteVersionDialog";
import { PromoteDialog } from "@/features/jobs/dialogs/PromoteDialog";
import { SetStatusDialog } from "@/features/jobs/dialogs/SetStatusDialog";

export interface VersionActionsProps {
  docUid: string;
  familyId: string;
  status: DocumentStatus;
}

type VersionDialog = "promote" | "status" | "delete";

export function VersionActions({ docUid, familyId, status }: VersionActionsProps) {
  return (
    <RequireRole level="A" fallback={null}>
      <VersionActionsMenu docUid={docUid} familyId={familyId} status={status} />
    </RequireRole>
  );
}

function VersionActionsMenu({ docUid, familyId, status }: VersionActionsProps) {
  const [dialog, setDialog] = useState<VersionDialog | null>(null);
  const lifecycle = useActionAvailability(false);
  const jobs = useActionAvailability(true);
  const close = (open: boolean) => {
    if (!open) {
      setDialog(null);
    }
  };
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${docUid}`}>
            <EllipsisIcon aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>{docUid}</DropdownMenuLabel>
          {status === "in_force" ? null : (
            <MenuAction
              icon={ArrowUpCircleIcon}
              label="Promote to in force…"
              reason={lifecycle.reason}
              onSelect={() => {
                setDialog("promote");
              }}
            />
          )}
          <MenuAction
            icon={ListChecksIcon}
            label="Set status…"
            reason={lifecycle.reason}
            onSelect={() => {
              setDialog("status");
            }}
          />
          <DropdownMenuSeparator />
          <MenuAction
            icon={Trash2Icon}
            label="Delete version…"
            destructive
            reason={jobs.reason}
            onSelect={() => {
              setDialog("delete");
            }}
          />
        </DropdownMenuContent>
      </DropdownMenu>
      <PromoteDialog open={dialog === "promote"} onOpenChange={close} docUid={docUid} familyId={familyId} />
      <SetStatusDialog open={dialog === "status"} onOpenChange={close} docUid={docUid} status={status} />
      <DeleteVersionDialog open={dialog === "delete"} onOpenChange={close} docUid={docUid} status={status} />
    </>
  );
}

interface MenuActionProps {
  icon: typeof Trash2Icon;
  label: string;
  reason: string | null;
  destructive?: boolean;
  onSelect: () => void;
}

function MenuAction({ icon: Icon, label, reason, destructive = false, onSelect }: MenuActionProps) {
  return (
    <DropdownMenuItem
      disabled={reason !== null}
      variant={destructive ? "destructive" : "default"}
      onSelect={onSelect}
    >
      <Icon aria-hidden />
      <span className="flex min-w-0 flex-col">
        <span>{label}</span>
        {reason === null ? null : <span className="text-label text-muted-foreground">{reason}</span>}
      </span>
    </DropdownMenuItem>
  );
}
